/**
 * Client for the Netlify Functions API. When the API is unreachable (static
 * preview, local `vite` without functions) the app switches to offline mode:
 * everything still works locally and the RFQ is shown as a draft.
 */
import { APP_CONFIG } from '@/config';
import type { ApiError, RfqPayload, RfqSubmitResponse } from '@shared/rfq';

let apiAvailable: Promise<boolean> | null = null;

export function isApiAvailable(): Promise<boolean> {
  apiAvailable ??= (async () => {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 2500);
      const res = await fetch(`${APP_CONFIG.apiBase}/health`, { signal: ctrl.signal, headers: { accept: 'application/json' } });
      clearTimeout(t);
      if (!res.ok) return false;
      const body = (await res.json()) as { ok?: boolean };
      return body.ok === true;
    } catch {
      return false;
    }
  })();
  return apiAvailable;
}

export interface UploadResult {
  id: string;
  sha256: string;
}

export async function uploadFile(file: Blob, fileName: string, purpose: 'artwork' | 'mockup'): Promise<UploadResult> {
  const res = await fetch(`${APP_CONFIG.apiBase}/artwork`, {
    method: 'POST',
    headers: {
      'content-type': file.type || 'application/octet-stream',
      'x-file-name': encodeURIComponent(fileName),
      'x-upload-purpose': purpose,
    },
    body: file,
  });
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; id?: string; sha256?: string; error?: string };
  if (!res.ok || !body.ok || !body.id) throw new Error(body.error ?? `upload_${res.status}`);
  return { id: body.id, sha256: body.sha256 ?? '' };
}

export async function submitRfq(payload: RfqPayload): Promise<RfqSubmitResponse | ApiError> {
  try {
    const res = await fetch(`${APP_CONFIG.apiBase}/rfq`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = (await res.json().catch(() => null)) as RfqSubmitResponse | ApiError | null;
    if (body) return body;
    return { ok: false, error: `http_${res.status}` };
  } catch {
    return { ok: false, error: 'network' };
  }
}
